<template>
    <v-dialog v-model="show" max-width="390" persistent no-click-animation>
        <div class="ScenarioImport">
            <!--Header dialog-->
            <div class="dialog-header">
                <div>{{ $f.tc('import') }}</div>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <!--Content dialog-->
            <v-form v-model="formValid" ref="form" @submit.prevent="submit" class="dialog-content">
                <v-text-field
                    class="input-custom"
                    :placeholder="$f.tc('chooseFile')"
                    v-model="fileName"
                    :rules="formRules.required"
                    spellcheck="false"
                    @click="pickFile"
                    clearable
                ></v-text-field>
                <div class="err-msg" v-if="showErrMessage">{{ $f.tc('pleaseUploadLatestVersion') }}</div>
                <input type="file" ref="file" accept=".json" v-show="false" @change="onFilePicked">
                <div class="mt-12" v-if="showOverwriteNLPConfigs">{{$f.tc('pleaseSelectBeforeImport')}}:</div>
                <v-checkbox
                    hide-details
                    v-model="isOverwriteNLPConfigs"
                    color="#0052CC"
                    :label="`${$f.tc('overwrite')} ${$t('nlpConfigs')}`"
                    v-if="showOverwriteNLPConfigs"
                ></v-checkbox>
            </v-form>
            <!--Dialog footer-->
            <div class="dialog-footer">
                <div class="btn-custom mr-8" @click="submit">{{ $f.tc('import') }}</div>
                <div class="btn-outline" @click="close">{{ $f.tc('cancel') }}</div>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import DialogPromiseFormMixin from "@/modules/fptai/mixins/DialogPromiseFormMixin";
import FormFileMixin from "@/modules/fptai/mixins/FormFileMixin";
import {mapState} from "vuex";

export default {
    name: "ScenarioImport",
    mixins: [DialogPromiseFormMixin, FormFileMixin],
    data() {
        return {
            fileContent: null,
            scenarioVersion: 2,
            isOverwriteNLPConfigs: true,
            showOverwriteNLPConfigs: false,
            showErrMessage: false,
            isConfirmUpdateVersion: false,
        }
    },
    computed: {
        ...mapState({
            scenarioSetting: state => state.scenario.scenario_setting,
        }),
    },
    methods: {
        open(...args) {
            this.clearForm()
            this.scenarioVersion = this.scenarioSetting.version
            this.isOverwriteNLPConfigs = true
            this.showOverwriteNLPConfigs = false
            this.showErrMessage = false
            this.show = true
            return new Promise((resolve, reject) => {
                this.promise = {resolve, reject}
            })
        },
        submitValidForm() {
            if (this.showErrMessage) {
                return
            }
            if (this.isConfirmUpdateVersion) {
                this.$appService.confirm(this.$f.tc('confirmUpdateVersion'), this.$f.tc('actionUndone')).then(
                    confirm => {
                        if (confirm) {
                            this.submitForm()
                        } else {
                            this.close()
                            this.clearForm()
                        }
                    }
                )
            } else {
                this.submitForm()
            }
        },
        submitForm() {
            this.promise.resolve({
                file: this.file,
                isOverwriteNLPConfigs: this.isOverwriteNLPConfigs
            })
            this.close()
            this.clearForm()
        },
        clearForm() {
            this.file = null
            this.fileName = null
            this.$refs.file.value = []
            this.fileContent = null
            this.$refs.form.reset()
        },
        // overwrite method from FormFileMixin
        onFilePicked() {
            const files = this.$refs.file.files
            if (files[0] === undefined) {
                return
            }
            this.file = files[0]
            this.fileName = files[0].name
        }
    },
    watch: {
        fileName: {
            deep: true,
            handler() {
                if (!this.fileName) {
                    this.file = null
                    this.$refs.file.value = []
                    this.fileContent = null
                }
            }
        },
        file: {
            deep: true,
            handler() {
                if (this.file) {
                    const fr = new FileReader()
                    fr.onload = e => {
                        const result = JSON.parse(e.target.result);
                        this.fileContent = result
                        let scenarioSetting = result.scenario.scenario_setting
                        let currentVersion = this.scenarioSetting.version
                        let versionImport = scenarioSetting.version ? scenarioSetting.version : 1
                        this.isConfirmUpdateVersion = currentVersion < versionImport
                        if (currentVersion === versionImport) { // case same version
                            if (currentVersion >= 2) {
                                this.showOverwriteNLPConfigs = true
                                this.isOverwriteNLPConfigs = false
                            }
                        } else if (currentVersion > versionImport) { // case version current > version import
                            this.showErrMessage = true
                        }
                    }
                    fr.readAsText(this.file)
                } else {
                    this.showErrMessage = false
                    this.showOverwriteNLPConfigs = false
                }
            }
        }
    }
}
</script>

<style scoped lang="scss">
.ScenarioImport {
    background: #FFFFFF;
    border-radius: 5px !important;
    overflow: hidden;

    .input-custom {
        ::v-deep {
            .v-input__slot {
                position: relative;
                background: #FFFFFF;
                border-radius: 5px;
                border: 1px solid #D9D9D9;
                outline: none;
                outline-offset: 0;
                font-size: 14px;
                margin: 0;
                display: flex;
                align-items: center;
                padding: 0 12px;

                &:before, &:after {
                    border: none !important;
                }
            }
        }
    }
}

.err-msg {
    font-size: 12px;
    color: #ff5252;
    margin-top: 2px;
    margin-left: 2px;
}

.dialog-content:has(.err-msg) {
    ::v-deep .input-custom {
        .v-input__slot {
            border-color: #ff5252 !important;
        }
    }
}

.dialog-content:has(.err-msg) {
    ::v-deep .input-custom.v-input--is-focused:not(.v-input--is-readonly) {
        .v-input__slot {
            outline: 2px solid #FF52526B;
            border-color: #ff5252 !important;
        }
    }
}

::v-deep {
    .v-label {
        font-size: 14px;
        color: #2c3e50;
    }

    .v-input--selection-controls {
        margin-top: 2px !important;
        padding-top: 0 !important;
    }

    .v-input--selection-controls__ripple {
        display: none;
    }

    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
        position: relative;
    }

    .v-input__append-inner {
        margin-top: 4px !important;
    }

    .v-text-field__details {
        position: absolute;
        right: 8px;
        top: 10px;
    }

    .input-custom:has(.error--text):not(.v-input--is-readonly) {
        .v-input__slot {
            border-color: #ff5252 !important;
        }
    }

    .input-custom.v-input--is-focused:not(.v-input--is-readonly) {
        .v-input__slot {
            outline: 2px solid #FF52526B;
        }

        &:not(.error--text) {
            .v-input__slot {
                outline: 2px solid #0052CC6B;
                border-color: #0052CC !important;
            }
        }
    }
}
</style>
